/**
 * ModernStatsScreen Component
 * Leaderboard only - shows all users ranked by balance
 */

import { useState, useEffect, useCallback } from 'react';
import { useUserContext } from '../contexts';
import { useTranslation } from '../hooks/useTranslation';
import { api } from '../services/api';
import { withTimeout } from '../utils/timeout';

interface LeaderboardEntry {
  rank: number;
  username: string;
  firstName: string;
  witonBalance: number;
  usdtBalance: number;
  referralCount: number;
  dailyIncome: number;
  isCurrentUser?: boolean;
}

interface PlatformStats {
  totalUsers: number;
  newToday: number;
  newThisWeek: number;
}

export function ModernStatsScreen() {
  const { user } = useUserContext();
  const { t } = useTranslation();
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [loadingLeaderboard, setLoadingLeaderboard] = useState(false);
  const [stats, setStats] = useState<PlatformStats | null>(null);

  const loadLeaderboard = useCallback(async () => {
    setLoadingLeaderboard(true);
    try {
      const response = await withTimeout(api.getLeaderboard('balance', 'all', 100), 10000);
      const data = response.data.leaderboard || response.data;
      const entries: LeaderboardEntry[] = data.map((entry: any, index: number) => ({
        rank: index + 1,
        username: entry.username || entry.firstName || 'Anonymous',
        firstName: entry.firstName || 'User',
        witonBalance: Number(entry.balance || entry.metricValue || 0),
        usdtBalance: Number(entry.usdtBalance || 0),
        referralCount: Number(entry.referralCount || 0),
        dailyIncome: Number(entry.dailyIncome || 0),
        isCurrentUser: user?.telegramUserId?.toString() === entry.telegramUserId?.toString(),
      }));
      setLeaderboard(entries);
    } catch (error) {
      console.error('Failed to load leaderboard:', error);
      setLeaderboard([]);
    } finally {
      setLoadingLeaderboard(false);
    }
  }, [user?.telegramUserId]);

  const loadStats = useCallback(async () => {
    try {
      const response = await api.getLeaderboardStats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  }, []);

  useEffect(() => {
    loadLeaderboard();
    loadStats();
  }, [loadLeaderboard, loadStats]);

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(180deg, #0a1628 0%, #000814 100%)', color: '#fff', paddingBottom: '80px' }}>
      {/* Header */}
      <div style={{ padding: '20px', textAlign: 'center', background: 'rgba(10, 22, 40, 0.8)', backdropFilter: 'blur(10px)' }}>
        <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>🏆 {t('leaderboard.title')}</h1>
      </div>

      {/* Leaderboard */}
      <div style={{ padding: '12px 16px' }}>
        {loadingLeaderboard ? (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: 'rgba(255, 255, 255, 0.6)' }}>
            <div style={{ fontSize: '18px' }}>Loading...</div>
          </div>
        ) : leaderboard.length > 0 ? (
          <>
            {leaderboard.map((entry) => (
              <div key={entry.rank} style={{
                background: entry.isCurrentUser ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, rgba(30, 58, 138, 0.3) 100%)' : 'rgba(10, 22, 40, 0.6)',
                borderRadius: '12px', padding: '16px', marginBottom: '8px',
                border: entry.isCurrentUser ? '2px solid #3b82f6' : '1px solid rgba(59, 130, 246, 0.2)',
                display: 'flex', alignItems: 'center', gap: '16px',
              }}>
                {/* Rank Badge */}
                <div style={{
                  width: '40px', height: '40px', borderRadius: '50%',
                  background: entry.rank <= 3
                    ? entry.rank === 1 ? 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)'
                    : entry.rank === 2 ? 'linear-gradient(135deg, #94a3b8 0%, #64748b 100%)'
                    : 'linear-gradient(135deg, #d97706 0%, #b45309 100%)'
                    : 'rgba(59, 130, 246, 0.3)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '18px', fontWeight: 700, flexShrink: 0,
                }}>
                  {entry.rank <= 3 ? (entry.rank === 1 ? '🥇' : entry.rank === 2 ? '🥈' : '🥉') : entry.rank}
                </div>

                {/* User Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {entry.username}
                    {entry.isCurrentUser && <span style={{ marginLeft: '8px', fontSize: '12px', color: '#3b82f6', fontWeight: 500 }}>(You)</span>}
                  </div>
                  <div style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.6)', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <span>{entry.witonBalance.toLocaleString()} WiTon</span>
                    <span style={{ color: 'rgba(255, 255, 255, 0.4)' }}>•</span>
                    <span>{entry.usdtBalance.toFixed(2)} USDT</span>
                    <span style={{ color: 'rgba(255, 255, 255, 0.4)' }}>•</span>
                    <span>👥 {entry.referralCount}</span>
                  </div>
                  {entry.dailyIncome > 0 && (
                    <div style={{ fontSize: '11px', color: '#10b981', marginTop: '2px' }}>
                      ⛏️ +{entry.dailyIncome.toLocaleString()} WiTon/day
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* TOP 100 Holders label */}
            <div style={{
              textAlign: 'center',
              padding: '16px 0',
              color: 'rgba(255, 255, 255, 0.5)',
              fontSize: '14px',
              fontWeight: 600,
              letterSpacing: '1px',
            }}>
              TOP 100 Holders
            </div>

            {/* Platform Stats */}
            {stats && (
              <div style={{
                background: 'rgba(10, 22, 40, 0.6)',
                borderRadius: '12px',
                padding: '16px',
                border: '1px solid rgba(59, 130, 246, 0.2)',
                marginTop: '8px',
              }}>
                <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px', textAlign: 'center', color: 'rgba(255,255,255,0.8)' }}>
                  📊 Platform Statistics
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', textAlign: 'center' }}>
                  <div>
                    <div style={{ fontSize: '20px', fontWeight: 700, color: '#3b82f6' }}>{stats.totalUsers.toLocaleString()}</div>
                    <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>Total Users</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '20px', fontWeight: 700, color: '#10b981' }}>+{stats.newToday}</div>
                    <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>Today</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '20px', fontWeight: 700, color: '#f59e0b' }}>+{stats.newThisWeek}</div>
                    <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>This Week</div>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: 'rgba(255, 255, 255, 0.6)' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🏆</div>
            <div style={{ fontSize: '18px' }}>No leaderboard data</div>
          </div>
        )}
      </div>
    </div>
  );
}
